// Citation formatters for bibliography export.
// All formatters return plain strings; the UI wraps them in <pre> for download.

const TYPE_LABELS = {
  book: 'Book',
  article: 'Article',
  chapter: 'Chapter',
  conference: 'Conference paper',
};

export function typeLabel(type) {
  return TYPE_LABELS[type] ?? type;
}

// Format an authors array as "Last, First; Last, First".
function joinAuthors(authors, sep = '; ') {
  return (authors ?? []).join(sep);
}

// ───────────────────────── CSV ─────────────────────────

const CSV_COLUMNS = [
  'id',
  'type',
  'title',
  'authors',
  'year',
  'language',
  'language_display',
  'keywords',
  'abstract',
  'doi',
  'url',
  'publisher',
  'place',
  'edition',
  'journal',
  'volume',
  'issue',
  'pages',
  'editors',
  'booktitle',
  'conference',
];

function csvField(v) {
  if (v == null) return '';
  const s = Array.isArray(v) ? v.join('; ') : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function toCSV(entries) {
  const lines = [CSV_COLUMNS.join(',')];
  for (const e of entries) {
    lines.push(CSV_COLUMNS.map((c) => csvField(e[c])).join(','));
  }
  return lines.join('\n');
}

// ───────────────────────── RIS ─────────────────────────

const RIS_TYPE = {
  book: 'BOOK',
  article: 'JOUR',
  chapter: 'CHAP',
  conference: 'CONF',
};

function risLine(tag, value) {
  return value == null || value === '' ? '' : `${tag}  - ${value}\n`;
}

function risEntry(e) {
  const lines = [];
  lines.push(`TY  - ${RIS_TYPE[e.type] ?? 'GEN'}\n`);
  for (const a of e.authors ?? []) lines.push(risLine('AU', a));
  for (const ed of e.editors ?? []) lines.push(risLine('ED', ed));
  lines.push(risLine('TI', e.title));
  lines.push(risLine('PY', e.year));
  lines.push(risLine('JO', e.journal));
  lines.push(risLine('VL', e.volume));
  lines.push(risLine('IS', e.issue));
  lines.push(risLine('SP', e.pages));
  lines.push(risLine('PB', e.publisher));
  lines.push(risLine('CY', e.place));
  lines.push(risLine('ET', e.edition));
  lines.push(risLine('BT', e.booktitle));
  lines.push(risLine('T2', e.conference));
  lines.push(risLine('LA', e.language_display ?? e.language));
  lines.push(risLine('DO', e.doi));
  lines.push(risLine('UR', e.url));
  lines.push(risLine('AB', e.abstract));
  for (const k of e.keywords ?? []) lines.push(risLine('KW', k));
  lines.push('ER  - \n');
  return lines.join('');
}

export function toRIS(entries) {
  return entries.map(risEntry).join('\n');
}

// ─────────────────────── Chicago (Author-Date) ───────────────────────

function chicagoAuthors(authors) {
  if (!authors?.length) return '';
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) return `${authors[0]}, and ${authors[1]}`;
  const head = authors.slice(0, -1).join(', ');
  return `${head}, and ${authors.at(-1)}`;
}

function chicagoBook(e) {
  const parts = [];
  if (e.authors?.length) parts.push(chicagoAuthors(e.authors) + '.');
  if (e.year) parts.push(e.year + '.');
  parts.push(`*${e.title}*.`);
  if (e.edition) parts.push(`${e.edition}.`);
  if (e.place || e.publisher) parts.push([e.place, e.publisher].filter(Boolean).join(': ') + '.');
  return parts.join(' ');
}

function chicagoArticle(e) {
  const parts = [];
  if (e.authors?.length) parts.push(chicagoAuthors(e.authors) + '.');
  if (e.year) parts.push(e.year + '.');
  parts.push(`"${e.title}."`);
  if (e.journal) parts.push(`*${e.journal}*`);
  const vi = [e.volume, e.issue ? `(${e.issue})` : null].filter(Boolean).join(' ');
  const tail = [vi, e.pages].filter(Boolean).join(': ');
  if (tail) parts.push(tail + '.');
  return parts.join(' ');
}

function chicagoChapter(e) {
  const parts = [];
  if (e.authors?.length) parts.push(chicagoAuthors(e.authors) + '.');
  if (e.year) parts.push(e.year + '.');
  parts.push(`"${e.title}."`);
  if (e.booktitle) parts.push(`In *${e.booktitle}*,`);
  if (e.editors?.length) parts.push(`edited by ${chicagoAuthors(e.editors)},`);
  if (e.pages) parts.push(`${e.pages}.`);
  if (e.place || e.publisher) parts.push([e.place, e.publisher].filter(Boolean).join(': ') + '.');
  return parts.join(' ');
}

function chicagoConference(e) {
  const parts = [];
  if (e.authors?.length) parts.push(chicagoAuthors(e.authors) + '.');
  if (e.year) parts.push(e.year + '.');
  parts.push(`"${e.title}."`);
  if (e.conference) parts.push(`Paper presented at *${e.conference}*,`);
  if (e.place) parts.push(e.place + '.');
  return parts.join(' ');
}

export function toChicago(entries) {
  return entries
    .map((e) => {
      switch (e.type) {
        case 'book':
          return chicagoBook(e);
        case 'article':
          return chicagoArticle(e);
        case 'chapter':
          return chicagoChapter(e);
        case 'conference':
          return chicagoConference(e);
        default:
          return e.title;
      }
    })
    .join('\n\n');
}

// ─────────────────────── MLA (9th edition, simplified) ───────────────────────

function mlaAuthors(authors) {
  if (!authors?.length) return '';
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) return `${authors[0]}, and ${authors[1]}`;
  return `${authors[0]}, et al.`;
}

function mlaBook(e) {
  const parts = [];
  if (e.authors?.length) parts.push(mlaAuthors(e.authors) + '.');
  parts.push(`*${e.title}*.`);
  if (e.edition) parts.push(`${e.edition}, `);
  if (e.publisher) parts.push(`${e.publisher},`);
  if (e.year) parts.push(`${e.year}.`);
  return parts.join(' ');
}

function mlaArticle(e) {
  const parts = [];
  if (e.authors?.length) parts.push(mlaAuthors(e.authors) + '.');
  parts.push(`"${e.title}."`);
  if (e.journal) parts.push(`*${e.journal}*,`);
  if (e.volume) parts.push(`vol. ${e.volume},`);
  if (e.issue) parts.push(`no. ${e.issue},`);
  if (e.year) parts.push(`${e.year},`);
  if (e.pages) parts.push(`pp. ${e.pages}.`);
  return parts.join(' ');
}

function mlaChapter(e) {
  const parts = [];
  if (e.authors?.length) parts.push(mlaAuthors(e.authors) + '.');
  parts.push(`"${e.title}."`);
  if (e.booktitle) parts.push(`*${e.booktitle}*,`);
  if (e.editors?.length) parts.push(`edited by ${mlaAuthors(e.editors)},`);
  if (e.publisher) parts.push(`${e.publisher},`);
  if (e.year) parts.push(`${e.year},`);
  if (e.pages) parts.push(`pp. ${e.pages}.`);
  return parts.join(' ');
}

function mlaConference(e) {
  const parts = [];
  if (e.authors?.length) parts.push(mlaAuthors(e.authors) + '.');
  parts.push(`"${e.title}."`);
  if (e.conference) parts.push(`*${e.conference}*,`);
  if (e.place) parts.push(`${e.place},`);
  if (e.year) parts.push(`${e.year}.`);
  return parts.join(' ');
}

export function toMLA(entries) {
  return entries
    .map((e) => {
      switch (e.type) {
        case 'book':
          return mlaBook(e);
        case 'article':
          return mlaArticle(e);
        case 'chapter':
          return mlaChapter(e);
        case 'conference':
          return mlaConference(e);
        default:
          return e.title;
      }
    })
    .join('\n\n');
}

// ───────────────────────── Download helper ─────────────────────────

export function downloadText(content, filename, mime = 'text/plain') {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
